import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import ModuleTopBar from "./ModuleTopBar";
import { AlertTriangle } from "lucide-react";
import FibiEspera from "./FibiEspera";   // v101.196.0 · clientes de WhatsApp en espera

// v3.8.0 · FIBI purgado del web también. Tour deshabilitado sin excepciones.

/**
 * Layout Odoo-Style
 * ─────────────────────────────────────────────────────────
 * Sin sidebar lateral. Un único ModuleTopBar con:
 *  - Botón Apps (→ launcher)
 *  - Módulo activo + dropdown submenú
 *  - Selector rápido de módulos
 *  - Notificaciones + perfil + avatar
 *
 * El contenido ocupa el ancho completo con contenedor
 * centrado y respiración adecuada.
 */
export default function Layout({ title, subtitle, actions, children, wide = false, hideChrome = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Forzar cambio de contraseña en primer login
  useEffect(() => {
    if (user?.must_change_password && location.pathname !== "/perfil") {
      navigate("/perfil?force_change=1", { replace: true });
    }
  }, [user?.must_change_password, location.pathname, navigate]);

  // Fullscreen mode (War Room) — no chrome, no padding
  if (hideChrome) {
    return (
      <div className="min-h-screen relative" style={{ background: "var(--bg-app)" }} data-testid="app-layout">
        {children}
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative"
      style={{ background: "var(--bg-app)" }}
      data-testid="app-layout"
    >
      {/* Scanline animated line */}
      <div className="scanline-fx" aria-hidden />

      <ModuleTopBar title={title} subtitle={subtitle} actions={actions} />

      <main className={`${wide ? "w-full" : "max-w-[1500px] mx-auto"} px-4 sm:px-6 lg:px-8 py-6 fade-up relative`}>
        {/* Título de página (secundario) — se muestra bajo el topbar si se define */}
        {title && (
          <div className="mb-5 flex items-end justify-between flex-wrap gap-3" data-testid="page-title-block">
            <div>
              {subtitle && (
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] neon-text-cyan mb-1">
                  {subtitle}
                </div>
              )}
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "JetBrains Mono", color: "var(--text-main)" }} data-testid="page-title">
                {title}
              </h1>
            </div>
          </div>
        )}

        {user?.must_change_password && (
          <div className="mb-5 p-4 flex items-start gap-3 neon-panel" style={{ borderColor: "var(--status-warn)", boxShadow: "0 0 0 1px var(--status-warn), 0 0 24px rgba(255,184,0,0.25)" }} data-testid="must-change-pw-banner">
            <AlertTriangle className="text-[var(--status-warn)] flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="font-bold text-sm uppercase tracking-wider font-mono" style={{ color: "var(--status-warn)" }}>Debes cambiar tu contraseña</div>
              <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                Por seguridad, en el primer inicio de sesión es obligatorio definir una contraseña personal. No podrás acceder al resto del sistema hasta hacerlo.
              </div>
            </div>
            <button
              onClick={() => navigate("/perfil?force_change=1")}
              className="neon-btn-yellow neon-btn"
            >
              Cambiar ahora →
            </button>
          </div>
        )}

        {children}
      </main>
      <FibiEspera />
    </div>
  );
}
